import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    experience_type: 'custom_itinerary',
    dates: '',
    guests: 1,
    message: ''
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      toast.success(response.data.message);
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/booking`, bookingForm);
      toast.success(response.data.message);
      setBookingForm({
        name: '',
        email: '',
        phone: '',
        experience_type: 'custom_itinerary',
        dates: '',
        guests: 1,
        message: ''
      });
    } catch (error) {
      toast.error('Failed to submit booking request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="w-full" data-testid="contact-page">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_fifa-fan-guides/artifacts/uctjfngs_IMG_6597.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <motion.div {...fadeInUp}>
            <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl text-white mb-6" data-testid="page-title">
              Get In Touch
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Let's plan your next adventure together
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="contact-info-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <MapPin className="w-10 h-10 text-[#1E6FBA] mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Location</h3>
            <p className="text-[#4A4A4A]">Penticton, BC, Canada</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Mail className="w-10 h-10 text-[#1E6FBA] mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Email</h3>
            <p className="text-[#4A4A4A]">info@anrealtravel.ca</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Phone className="w-10 h-10 text-[#1E6FBA] mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Phone</h3>
            <p className="text-[#4A4A4A]">+1 (250) 486-3888</p>
          </motion.div>
        </div>
      </section>

      {/* Forms */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="forms-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* General Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <h2 className="text-3xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                General Inquiry
              </h2>
              <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="contact-name">Your Name</Label>
                  <Input
                    id="contact-name"
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email">Email</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-message">Message</Label>
                  <Textarea
                    id="contact-message"
                    required
                    rows={5}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="Tell us how we can help..."
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-scale bg-[#1E6FBA] hover:bg-[#1558A0] text-white py-6"
                  disabled={loading}
                  data-testid="contact-submit-button"
                >
                  <Send className="mr-2 w-5 h-5" />
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Booking Request Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-[#F5E9D4] p-8 rounded-2xl grain-texture">
              <h2 className="text-3xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Booking Request
              </h2>
              <form onSubmit={handleBookingSubmit} className="space-y-6" data-testid="booking-form">
                <div>
                  <Label htmlFor="booking-name">Your Name</Label>
                  <Input
                    id="booking-name"
                    type="text"
                    required
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                    data-testid="booking-name-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="booking-email">Email</Label>
                    <Input
                      id="booking-email"
                      type="email"
                      required
                      value={bookingForm.email}
                      onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                      data-testid="booking-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-phone">Phone</Label>
                    <Input
                      id="booking-phone"
                      type="tel"
                      required
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                      data-testid="booking-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="booking-type">Experience Type</Label>
                  <select
                    id="booking-type"
                    required
                    value={bookingForm.experience_type}
                    onChange={(e) => setBookingForm({ ...bookingForm, experience_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E6FBA]"
                    data-testid="booking-type-select"
                  >
                    <option value="walking_tour">Walking Tour</option>
                    <option value="custom_itinerary">Custom Itinerary</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="booking-dates">Preferred Dates</Label>
                    <Input
                      id="booking-dates"
                      type="text"
                      value={bookingForm.dates}
                      onChange={(e) => setBookingForm({ ...bookingForm, dates: e.target.value })}
                      placeholder="e.g., July 15-20"
                      data-testid="booking-dates-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-guests">Number of Guests</Label>
                    <Input
                      id="booking-guests"
                      type="number"
                      min="1"
                      required
                      value={bookingForm.guests}
                      onChange={(e) => setBookingForm({ ...bookingForm, guests: parseInt(e.target.value) })}
                      data-testid="booking-guests-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="booking-message">Additional Details (Optional)</Label>
                  <Textarea
                    id="booking-message"
                    rows={4}
                    value={bookingForm.message}
                    onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                    placeholder="Tell us more about what you're looking for..."
                    data-testid="booking-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-scale bg-[#2E8B57] hover:bg-[#26734B] text-white py-6"
                  disabled={loading}
                  data-testid="booking-submit-button"
                >
                  <Send className="mr-2 w-5 h-5" />
                  {loading ? 'Submitting...' : 'Request Booking'}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
